// Portal-Outlets der generischen App-Shell — die Konfigurations-API für
// Routen: Die Shell (__root) rendert die Bars, Seiten liefern nur
// Inhalte per createPortal in die Outlet-DOM-Knoten. Kein JSX im zustand-
// Store (Stale-Closure-Falle), kein Prop-Threading. Nutzung pro Route:
//
//   useMobilePageTitle('Projekte')            // Top-Mitte (Titel-String)
//
//   <MobileBottomSlots>                       // Slots hinter dem ☰
//     <BarSlot label="Suche" icon={<Search/>} onClick={…} />
//     <BarAccentPlus ariaLabel="Neues Projekt" onClick={…} />
//   </MobileBottomSlots>
//
//   <MobileTopCenter>…Tabs oder ◀ Titel ▶…</MobileTopCenter>  // optional
//
// Eine Route OHNE Registrierung bekommt automatisch die Minimal-Bar
// (☰ unten + Fallback-Titel oben via lib/mobile-title.ts) — nichts kann
// „vergessen kaputt" sein.
//
// Sichtbarkeits-Gates (T3, Slot-Adoption im Tablet-Band):
//   - MobileBottomSlots registriert bei useShellBars() = Phone ODER
//     Tablet-Band 640–1023 — die Bottom-Bar existiert in beiden Welten.
//     Doppel-UI im Band ist ENTSCHEIDUNG, kein Versehen: die Desktop-Köpfe
//     der Seiten bleiben sichtbar (phone:hidden greift nicht), ＋/Filter
//     existieren also doppelt (Kopf + Bar) — Daumen-Redundanz auf Tablets,
//     beide Wege nutzen dieselben Handler/States der Seite.
//   - MobileTopCenter bleibt isPhone-gated: die Top-Leiste (MobileShellTop)
//     wird im Band NICHT gerendert, ihr Portal-Ziel existiert dort nicht.
//     Band-Ersatz sind die Desktop-Köpfe (◀▶-Nav, Zurück-Links).
//
// Die Outlet-Elemente + Mount-Zähler leben in einem eigenen kleinen Store
// (Implementierungsdetail der Shell) — der route-orientierte Shell-State
// (Drawer/Notif/Titel/suppressBottomBar) bleibt in lib/shell-store.ts.

import { type ReactNode, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { create } from 'zustand'
import { useShellStore } from '../../lib/shell-store'
import { useIsMobile, useShellBars } from '../../lib/use-media-query'

interface ShellOutletStore {
  /** Ziel-DOM-Knoten der Top-Mitte (von MobileShellTop per ref gesetzt). */
  topCenterEl: HTMLElement | null
  setTopCenterEl: (el: HTMLElement | null) => void
  /** Anzahl gemounteter MobileTopCenter — >0 blendet den Default-Titel aus. */
  topCenterCount: number
  addTopCenter: () => void
  removeTopCenter: () => void

  /** Ziel-DOM-Knoten der Bottom-Slots (von MobileShellBottom per ref gesetzt). */
  bottomSlotsEl: HTMLElement | null
  setBottomSlotsEl: (el: HTMLElement | null) => void
  /** Anzahl gemounteter MobileBottomSlots — 0 = Minimal-Bar (nur ☰). */
  bottomSlotsCount: number
  addBottomSlots: () => void
  removeBottomSlots: () => void
}

export const useShellOutletStore = create<ShellOutletStore>((set) => ({
  topCenterEl: null,
  setTopCenterEl: (el) => set({ topCenterEl: el }),
  topCenterCount: 0,
  addTopCenter: () => set((s) => ({ topCenterCount: s.topCenterCount + 1 })),
  removeTopCenter: () => set((s) => ({ topCenterCount: Math.max(0, s.topCenterCount - 1) })),

  bottomSlotsEl: null,
  setBottomSlotsEl: (el) => set({ bottomSlotsEl: el }),
  bottomSlotsCount: 0,
  addBottomSlots: () => set((s) => ({ bottomSlotsCount: s.bottomSlotsCount + 1 })),
  removeBottomSlots: () => set((s) => ({ bottomSlotsCount: Math.max(0, s.bottomSlotsCount - 1) })),
}))

/** Setzt den Titel der mobilen Top-Leiste für die Lebensdauer der Seite
 *  (Mount setzt, Unmount cleart → nächste Route fällt sauber auf ihren
 *  eigenen Titel bzw. den Nav-Label-Fallback zurück).
 *
 *  `null` = nicht registrieren (No-op) — für Seiten, die auch als Tab in
 *  einer Host-Seite eingebettet laufen (z.B. CustomersPage in /stammdaten):
 *  eingebettet gehört der Titel dem Host, standalone der Seite selbst. */
export function useMobilePageTitle(title: string | null) {
  const setTitle = useShellStore((s) => s.setTitle)
  useLayoutEffect(() => {
    if (title === null) return
    setTitle(title)
    return () => setTitle(null)
  }, [title, setTitle])
}

/** Portalt Seiten-Slots in die Bottom-Bar der Shell — gerendert, wo die Bar
 *  existiert: Phone (hoch + quer) UND Tablet-Band 640–1023 (useShellBars,
 *  T3-Adoption); State und Handler bleiben in der Seite. Kinder sind
 *  BarSlot/BarAccentPlus aus ui/mobile-action-bar.tsx; der Outlet-Knoten ist
 *  display:contents, die Slots werden also echte Flex-Kinder der Bar-Zeile.
 *  Im Band entsteht bewusst Doppel-UI (Kopf-Buttons + Bar-Slots, gleiche
 *  Handler) — siehe Header-Kommentar. */
export function MobileBottomSlots({ children }: { children: ReactNode }) {
  const barsVisible = useShellBars()
  const el = useShellOutletStore((s) => s.bottomSlotsEl)
  const add = useShellOutletStore((s) => s.addBottomSlots)
  const remove = useShellOutletStore((s) => s.removeBottomSlots)

  // Registrierung synchron vor dem Paint (useLayoutEffect), damit die
  // Minimal-Bar-Platzhalter der Shell nicht einen Frame neben den frisch
  // portalten Slots aufblitzen.
  useLayoutEffect(() => {
    if (!barsVisible) return
    add()
    return remove
  }, [barsVisible, add, remove])

  // el ist null, solange die Bar nicht gemountet ist (z.B. suppressBottomBar
  // beim Chat-Composer-Fokus, oder ausgeloggt) — dann bewusst No-op statt
  // createPortal in ein totes Ziel.
  if (!barsVisible || !el) return null
  return createPortal(children, el)
}

/** Portalt eine reiche Top-Mitte (z.B. Tabs oder ◀ Titel ▶) in die
 *  Top-Leiste der Shell — ersetzt dort den Default-Titel-String.
 *  Bewusst PHONE-ONLY (nicht useShellBars): im Tablet-Band gibt es keine
 *  MobileShellTop, das Portal-Ziel (topCenterEl) bliebe dort null — das
 *  isPhone-Gate hält zusätzlich den Mount-Zähler sauber und die Effekte
 *  der Kinder vom Band fern. Selbst OHNE Gate wäre es kein Crash: der
 *  `!el`-Guard unten macht das Portal zum No-op. */
export function MobileTopCenter({ children }: { children: ReactNode }) {
  const isPhone = useIsMobile()
  const el = useShellOutletStore((s) => s.topCenterEl)
  const add = useShellOutletStore((s) => s.addTopCenter)
  const remove = useShellOutletStore((s) => s.removeTopCenter)

  useLayoutEffect(() => {
    if (!isPhone) return
    add()
    return remove
  }, [isPhone, add, remove])

  if (!isPhone || !el) return null
  return createPortal(children, el)
}
